import React, { useState, useEffect, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Formik,
  Form,
  Field,
  yupToFormErrors,
  setNestedObjectValues
} from 'formik';
import Input from '@hobs/components/Input';
import Money from '@hobs/components/Money';
import cx from 'classnames';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import Icon from '@hobs/components/Icon';
import Dropdown from '@hobs/components/Dropdown';
import Dialog from '@hobs/components/Dialog';
import DropZone from '@hobs/components/DropZone';
import Switch from '@hobs/components/Switch';
import Confirm from '@hobs/components/Confirm';
import * as Yup from 'yup';
import { withRouter } from 'react-router-dom';
import ShortId from 'shortid';
import './AssignmentResponse.scss';
import Quantity from '../Quantity';

const AssignmentResponse = ({
  assignmentResponse,
  assignmentDelivery,
  delivery = {},
  t,
  readOnly,
  onSubmit,
  onDeliverySubmit,
  vatOptions,
  getSelectedVat,
  disabled,
  isFileDialogOpen,
  onFileDialogOpen,
  onFileDialogClose,
  onFileSelect,
  isDeliveryFileDialogOpen,
  onDeliveryFileDialogOpen,
  onDeliveryFileDialogClose,
  onSharedCostChange,
  onEarlyConfirmationRequiredChange,
  onDeliveryFinalizedChange,
  onDeliveryRollback,
  isEarlyConfirmationVisible,
  exerciseCurrency,
  showCurrency
}) => {
  const [key, setKey] = useState(ShortId.generate());
  const [
    deliveryQuantityApprovalRequired,
    setDeliveryQuantityApprovalRequired
  ] = useState(false);

  // https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const prevDelivery = usePrevious(delivery);

  useEffect(() => {
    if (JSON.stringify(delivery) !== JSON.stringify(prevDelivery)) {
      onRollBackUpdate();
    }
  });

  const onRollBackUpdate = () => {
    setKey(ShortId.generate());
  };

  const onFormSubmit = (values, actions) => {
    if (values.SUBMIT_TYPE === 'RESPONSE') {
      values.unitCostDisplay =
        values.unitCostDisplay && values.unitCostDisplay.length
          ? values.unitCostDisplay
          : null;
      values.totalCostDisplay =
        values.totalCostDisplay && values.totalCostDisplay.length
          ? values.totalCostDisplay
          : null;

      Yup.object()
        .shape({
          canDeliveryQuantity: Yup.number()
            .required(t('Quantity is required'))
            .typeError('Invalid number')
            .min(0, t('Quantity must be greater than 0'))
            .nullable(),
          comment: Yup.string()
            .max(1500, t('Comment cannot be longer than 1500 symbols'))
            .nullable(),
          unitCostDisplay: Yup.number()
            .min(0, t('Price must be greater than 0'))
            .nullable(),
          totalCostDisplay: Yup.number()
            .min(0, t('Total must be greater than 0'))
            .nullable()
        })
        .validate(values, { abortEarly: false })
        .catch(errors => {
          actions.setErrors(yupToFormErrors(errors));
          actions.setTouched(setNestedObjectValues(values, true));
          actions.setSubmitting(false);
        })
        .then(response => {
          if (response) {
            onSubmit(values, actions);
          }
        });
    }

    if (values.SUBMIT_TYPE === 'DELIVERY') {
      Yup.object()
        .shape({
          delivery: Yup.object().shape({
            deliveryQuantity: Yup.number()
              .nullable()
              .required(t('Quantity is required'))
              .typeError('Quantity is required')
              .positive(t('Quantity must be greater than 0')),
            unitCostDisplay: Yup.number()
              .required(t('Price is required'))
              .typeError('Price is required')
              .min(0, t('Price must be equal to or greater than 0'))
              .nullable(),
            totalCostDisplay: Yup.number()
              .required(t('Total cost is required'))
              .typeError('Total cost is required')
              .min(0, t('Total cost must be equal to or greater than 0'))
              .nullable(),
            valueAddedTaxId: Yup.string()
              .required(t('VAT is required'))
              .nullable(),
            comment: Yup.string()
              .max(1500, t('Comment cannot be longer than 500 symbols'))
              .nullable()
          })
        })
        .validate(values, { abortEarly: false })
        .catch(errors => {
          actions.setErrors(yupToFormErrors(errors));
          actions.setTouched(setNestedObjectValues(values, true));
          actions.setSubmitting(false);
        })
        .then(response => {
          if (response) {
            if (!isQuantityApprovalRequired(values)) {
              onDeliverySubmit(values.delivery, actions);
            } else {
              setDeliveryQuantityApprovalRequired(true);
            }
          }
        });
    }
  };

  function isQuantityApprovalRequired(values) {
    return values.canDeliveryQuantity < values.delivery.deliveryQuantity;
  }

  function getDefaultVat() {
    return getSelectedVat ? getSelectedVat().valueAddedTaxId : null;
  }

  function getResponseValues(assignmentResponse) {
    var attachments = [];

    if (
      assignmentDelivery &&
      assignmentDelivery.attachments &&
      assignmentDelivery.attachments.length > 0
    ) {
      attachments = assignmentDelivery.attachments;
    }

    if (delivery && delivery.attachments && delivery.attachments.length > 0) {
      attachments = delivery.attachments;
    }

    return {
      ...assignmentResponse,
      delivery: delivery
        ? {
            ...delivery,
            attachments: attachments,
            unitCostDisplay: delivery.unitCost
              ? delivery.unitCost.toFixed(2)
              : delivery.unitCost,
            totalCostDisplay: delivery.totalCost
              ? delivery.totalCost.toFixed(2)
              : delivery.totalCost
          }
        : {},
      totalCostDisplay: assignmentResponse.totalCost,
      unitCostDisplay: assignmentResponse.unitCost,
      valueAddedTaxId: assignmentResponse.valueAddedTaxId
        ? assignmentResponse.valueAddedTaxId
        : getDefaultVat()
    };
  }

  function calcUnitCost(canDeliveryQuantity, totalCost, setFieldValue) {
    if (!canDeliveryQuantity || !totalCost) {
      setFieldValue('totalCost', null);
      setFieldValue('unitCost', null);
      return;
    }

    setFieldValue(
      'unitCostDisplay',
      (totalCost / canDeliveryQuantity).toFixed(2)
    );
    setFieldValue('totalCost', totalCost);
    setFieldValue('unitCost', totalCost / canDeliveryQuantity);
  }

  function calcTotalCost(canDeliveryQuantity, unitCost, setFieldValue) {
    if ((!canDeliveryQuantity && canDeliveryQuantity !== 0) || !unitCost) {
      setFieldValue('totalCost', null);
      setFieldValue('unitCost', null);
      return;
    }

    setFieldValue(
      'totalCostDisplay',
      (unitCost * canDeliveryQuantity).toFixed(2)
    );
    setFieldValue('totalCost', unitCost * canDeliveryQuantity);
    setFieldValue('unitCost', unitCost);
  }

  function calcDeliveryUnitCost(canDeliveryQuantity, totalCost, setFieldValue) {
    setFieldValue('delivery.totalCost', totalCost);

    if (!canDeliveryQuantity) {
      return;
    }
    if (!totalCost && totalCost !== 0) {
      totalCost = 0;
    }

    setFieldValue(
      'delivery.unitCostDisplay',
      (totalCost / canDeliveryQuantity).toFixed(2)
    );
    setFieldValue('delivery.unitCost', totalCost / canDeliveryQuantity);
  }

  function calcDeliveryTotalCost(canDeliveryQuantity, unitCost, setFieldValue) {
    setFieldValue('delivery.unitCost', unitCost);

    if (!unitCost && unitCost !== 0) {
      unitCost = 0;
    }
    if (!canDeliveryQuantity && canDeliveryQuantity !== 0) {
      canDeliveryQuantity = 0;
    }

    setFieldValue(
      'delivery.totalCostDisplay',
      (unitCost * canDeliveryQuantity).toFixed(2)
    );
    setFieldValue('delivery.totalCost', unitCost * canDeliveryQuantity);
  }

  function getInitialValues() {
    return assignmentResponse
      ? getResponseValues(assignmentResponse)
      : {
          canDeliveryQuantity: '',
          unitCostDisplay: '',
          unitCost: '',
          totalCost: '',
          totalCostDisplay: '',
          grossAmount: '',
          comment: '',
          isDraft: true,
          valueAddedTaxId: getDefaultVat(),
          attachments: [],
          delivery: {
            ...delivery,
            attachments: delivery.attachments ? delivery.attachments : [],
            unitCostDisplay: delivery.unitCost
              ? delivery.unitCost.toFixed(2)
              : delivery.unitCost,
            totalCostDisplay: delivery.totalCost
              ? delivery.totalCost.toFixed(2)
              : delivery.totalCost
          }
        };
  }

  return (
    <div className="assignment-response">
      <Formik
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize={true}
        initialValues={getInitialValues()}
        onSubmit={onFormSubmit}
        key={key}
      >
        {({
          handleSubmit,
          setFieldValue,
          values,
          isSubmitting,
          setSubmitting
        }) => {
          return (
            <Form noValidate={true}>
              <Dialog
                title={t('Response files')}
                isOpen={isFileDialogOpen}
                onClose={onFileDialogClose}
                className="response-files"
              >
                <DropZone
                  onSelect={files => {
                    onFileSelect(files).then(response => {
                      setFieldValue(
                        'attachments',
                        values.attachments.concat(response.payload)
                      );
                    });
                  }}
                  onRemove={(file, list) => {
                    setFieldValue('attachments', list);
                  }}
                  value={values.attachments ? values.attachments : []}
                  key={ShortId.generate()}
                  disabled={
                    disabled ||
                    (assignmentResponse && !assignmentResponse.canBeEdited)
                  }
                  showSuccessMessages={true}
                />
              </Dialog>
              <Dialog
                title={t('Delivery files')}
                isOpen={isDeliveryFileDialogOpen}
                onClose={onDeliveryFileDialogClose}
                className="response-files"
              >
                <DropZone
                  onSelect={files => {
                    onFileSelect(files).then(response => {
                      setFieldValue(
                        'delivery.attachments',
                        values.delivery.attachments.concat(response.payload)
                      );
                    });
                  }}
                  onRemove={(file, list) => {
                    setFieldValue('delivery.attachments', list);
                  }}
                  value={
                    values.delivery.attachments
                      ? values.delivery.attachments
                      : []
                  }
                  key={ShortId.generate()}
                  disabled={!values.delivery.isEditable || readOnly}
                  showSuccessMessages={true}
                />
              </Dialog>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('Delivery')}</th>
                    {showCurrency && <th>{t('Currency')}</th>}
                    <th>{t('Price')}</th>
                    <th>{t('Total')}</th>
                    <th>{t('VAT')}</th>
                    <th>{t('Gross amount')}</th>
                    <th>{t('Comment')}</th>
                    <th>{t('Files')}</th>
                    {!disabled && <th />}
                    {!disabled && <th />}
                  </tr>
                </thead>
                <tbody>
                  {assignmentResponse && (
                    <tr>
                      <td className="small">
                        {(disabled || !assignmentResponse.canBeEdited) && (
                          <Quantity amount={values.canDeliveryQuantity} />
                        )}
                        {!disabled && assignmentResponse.canBeEdited && (
                          <Field
                            name="canDeliveryQuantity"
                            component={Input}
                            noMargin
                            setFieldValue={setFieldValue}
                            min={0}
                            type="number"
                            onChange={event => {
                              calcTotalCost(
                                event.target.value,
                                values.unitCost,
                                setFieldValue
                              );
                            }}
                          />
                        )}
                      </td>
                      {showCurrency && (
                        <td className="small">
                          {(disabled || !assignmentResponse.canBeEdited) && (
                            <span>{exerciseCurrency}</span>
                          )}
                          {!disabled && assignmentResponse.canBeEdited && (
                            <Input
                              name="exerciseCurrency"
                              disabled={true}
                              noMargin
                              type="text"
                              value={exerciseCurrency}
                            />
                          )}
                        </td>
                      )}
                      <td className="small">
                        {(disabled || !assignmentResponse.canBeEdited) && (
                          <Money amount={values.unitCost} />
                        )}
                        {!disabled && assignmentResponse.canBeEdited && (
                          <>
                            <Field
                              name="unitCostDisplay"
                              component={Input}
                              noMargin
                              min={0}
                              setFieldValue={setFieldValue}
                              type="number"
                              onChange={event => {
                                calcTotalCost(
                                  values.canDeliveryQuantity,
                                  event.target.value,
                                  setFieldValue
                                );
                              }}
                            />
                            <Field name="unitCost" min={0} type="hidden" />
                          </>
                        )}
                      </td>
                      <td className="small">
                        {(disabled || !assignmentResponse.canBeEdited) && (
                          <Money amount={values.totalCost} />
                        )}
                        {!disabled && assignmentResponse.canBeEdited && (
                          <>
                            <Field
                              name="totalCostDisplay"
                              component={Input}
                              noMargin
                              min={0}
                              type="number"
                              setFieldValue={setFieldValue}
                              onChange={event => {
                                calcUnitCost(
                                  values.canDeliveryQuantity,
                                  event.target.value,
                                  setFieldValue
                                );
                              }}
                            />
                            <Field name="totalCost" min={0} type="hidden" />
                          </>
                        )}
                      </td>
                      <td className="medium">
                        {(disabled || !assignmentResponse.canBeEdited) && (
                          <span>{assignmentResponse.vatValue}%</span>
                        )}
                        {!disabled && assignmentResponse.canBeEdited && (
                          <Field
                            setFieldValue={setFieldValue}
                            name="valueAddedTaxId"
                            component={Dropdown}
                            disabled={
                              !values.canDeliveryQuantity ||
                              !values.unitCost ||
                              !values.totalCost
                            }
                          >
                            {vatOptions}
                          </Field>
                        )}
                      </td>
                      <td className="small">
                        {(disabled || !assignmentResponse.canBeEdited) && (
                          <Money amount={values.grossAmount} />
                        )}
                        {!disabled && assignmentResponse.canBeEdited && (
                          <Money
                            amount={
                              values.totalCost *
                              (getSelectedVat(values.valueAddedTaxId)
                                .percentage /
                                100 +
                                1)
                            }
                          />
                        )}
                      </td>
                      <td className="comment">
                        {(disabled || !assignmentResponse.canBeEdited) && (
                          <span>{values.comment}</span>
                        )}
                        {!disabled && assignmentResponse.canBeEdited && (
                          <Field
                            name="comment"
                            component={Input}
                            noMargin
                            setFieldValue={setFieldValue}
                          />
                        )}
                      </td>
                      <td className="small">
                        <div
                          onClick={onFileDialogOpen}
                          className="response-attachment-link"
                        >
                          <Icon
                            icon="attachment"
                            className={cx('files-icon', {
                              'no-files': !values.attachments.length
                            })}
                          />
                          {values.attachments.length
                            ? `(${values.attachments.length})`
                            : ''}
                        </div>
                      </td>
                      {!disabled && values.sharedCost ? (
                        <td>
                          <Switch
                            disabled={!values.sharedCost.isEditable}
                            checked={values.sharedCost.isSharedCost}
                            onChange={sharedCost => {
                              Confirm(
                                t('Update shared cost'),
                                t(
                                  'Are you sure you want to update shared cost?'
                                ),
                                () => {
                                  onSharedCostChange(sharedCost);
                                  setFieldValue(
                                    'sharedCost.isSharedCost',
                                    sharedCost
                                  );
                                  setFieldValue('isSharedCost', sharedCost);
                                }
                              );
                            }}
                            label={t('Shared cost')}
                          />
                        </td>
                      ) : (
                        <td></td>
                      )}
                      {!disabled &&
                      values.earlyConfirmation &&
                      isEarlyConfirmationVisible ? (
                        <td>
                          <Switch
                            disabled={!values.earlyConfirmation.isEditable}
                            checked={
                              values.earlyConfirmation
                                .isEarlyConfirmationRequired
                            }
                            onChange={isEarlyConfirmationRequired => {
                              Confirm(
                                t('Update early confirmation required'),
                                t(
                                  'Are you sure you want to update early confirmation required?'
                                ),
                                () => {
                                  onEarlyConfirmationRequiredChange();
                                  setFieldValue(
                                    'earlyConfirmation.isEarlyConfirmationRequired',
                                    isEarlyConfirmationRequired
                                  );
                                }
                              );
                            }}
                            label={t('Early confirmation required')}
                          />
                        </td>
                      ) : (
                        <td></td>
                      )}
                      {!disabled && (
                        <td className="controls">
                          <>
                            {(assignmentResponse.isDraft === null ||
                              assignmentResponse.isDraft === true) && (
                              <GhostButton
                                onClick={async () => {
                                  await setFieldValue(
                                    'SUBMIT_TYPE',
                                    'RESPONSE'
                                  );
                                  await setFieldValue('isDraft', true);
                                  handleSubmit();
                                }}
                                type="button"
                                disabled={isSubmitting}
                              >
                                {t('Save draft')}
                              </GhostButton>
                            )}
                            <RaisedButton
                              onClick={async () => {
                                await setFieldValue('SUBMIT_TYPE', 'RESPONSE');
                                await setFieldValue('isDraft', false);
                                handleSubmit();
                              }}
                              type="button"
                              disabled={
                                isSubmitting || !assignmentResponse.canBeEdited
                              }
                            >
                              {t('Send response')}
                            </RaisedButton>
                          </>
                        </td>
                      )}
                    </tr>
                  )}
                  {values.delivery.isVisible && (
                    <tr>
                      <td>
                        {readOnly && (
                          <Quantity amount={values.delivery.deliveryQuantity} />
                        )}
                        {!readOnly && (
                          <Field
                            noMargin
                            disabled={!values.delivery.isEditable || readOnly}
                            component={Input}
                            type="number"
                            name="delivery.deliveryQuantity"
                            onChange={event => {
                              calcDeliveryTotalCost(
                                event.target.value,
                                values.delivery.unitCost,
                                setFieldValue
                              );
                            }}
                          />
                        )}
                      </td>
                      {showCurrency && readOnly && (
                        <td>
                          <span>{exerciseCurrency}</span>
                        </td>
                      )}
                      {showCurrency && !readOnly && (
                        <td>
                          <Input
                            name="exerciseCurrency"
                            disabled={true}
                            noMargin
                            type="text"
                            value={exerciseCurrency}
                          />
                        </td>
                      )}
                      <td>
                        {readOnly && (
                          <Money amount={values.delivery.deliveryQuantity} />
                        )}
                        {!readOnly && (
                          <Field
                            name="delivery.unitCostDisplay"
                            component={Input}
                            noMargin
                            min={0}
                            disabled={!values.delivery.isEditable}
                            setFieldValue={setFieldValue}
                            type="number"
                            onChange={event => {
                              calcDeliveryTotalCost(
                                values.delivery.deliveryQuantity,
                                event.target.value,
                                setFieldValue
                              );
                            }}
                          />
                        )}
                        <Field name="delivery.unitCost" min={0} type="hidden" />
                      </td>
                      <td>
                        {readOnly && (
                          <Money amount={values.delivery.totalCostDisplay} />
                        )}
                        {!readOnly && (
                          <Field
                            name="delivery.totalCostDisplay"
                            component={Input}
                            noMargin
                            min={0}
                            type="number"
                            disabled={!values.delivery.isEditable}
                            setFieldValue={setFieldValue}
                            onChange={event => {
                              calcDeliveryUnitCost(
                                values.delivery.deliveryQuantity,
                                event.target.value,
                                setFieldValue
                              );
                            }}
                          />
                        )}
                        <Field
                          name="delivery.totalCost"
                          min={0}
                          type="hidden"
                        />
                      </td>
                      <td>
                        {readOnly && (
                          <Money amount={values.delivery.totalCostDisplay} />
                        )}
                        {!readOnly && (
                          <Field
                            noMargin
                            setFieldValue={setFieldValue}
                            component={Dropdown}
                            name="delivery.valueAddedTaxId"
                            disabled={!values.delivery.isEditable}
                          >
                            <option value="">{t('Select VAT')}</option>
                            {vatOptions}
                          </Field>
                        )}
                      </td>
                      <td>
                        <Money
                          amount={
                            values.delivery.totalCost *
                            (getSelectedVat(values.delivery.valueAddedTaxId)
                              .percentage /
                              100 +
                              1)
                          }
                        />
                      </td>
                      <td>
                        {readOnly && (
                          <Money amount={values.delivery.totalCostDisplay} />
                        )}
                        {!readOnly && (
                          <Field
                            noMargin
                            component={Input}
                            name="delivery.comment"
                            disabled={!values.delivery.isEditable}
                          />
                        )}
                      </td>
                      <td className="small">
                        <div
                          onClick={onDeliveryFileDialogOpen}
                          className="response-attachment-link"
                        >
                          <Icon
                            icon="attachment"
                            className={cx('files-icon', {
                              'no-files': !values.delivery.attachments.length
                            })}
                          />
                          {values.delivery.attachments.length
                            ? `(${values.delivery.attachments.length})`
                            : ''}
                        </div>
                      </td>
                      <td></td>
                      <td>
                        <Switch
                          disabled={!values.delivery.isEditable || readOnly}
                          checked={values.delivery.isDeliveryFinalized}
                          onChange={deliveryFinalized => {
                            Confirm(
                              t('Change delivery finalized status'),
                              deliveryFinalized
                                ? t(
                                    'Are you sure you want confirm delivery as finalized?'
                                  )
                                : t(
                                    'Are you sure you want reset delivery as not finalized?'
                                  ),
                              () => {
                                onDeliveryFinalizedChange(
                                  deliveryFinalized,
                                  () => {
                                    setFieldValue(
                                      'delivery.isDeliveryFinalized',
                                      deliveryFinalized
                                    );
                                  }
                                );
                              }
                            );
                          }}
                          label={t('Delivery finalized')}
                        />
                      </td>
                      <td className="controls">
                        <GhostButton
                          disabled={
                            isSubmitting ||
                            !values.delivery.isEditable ||
                            readOnly
                          }
                          onClick={async () => {
                            await setFieldValue('SUBMIT_TYPE', 'DELIVERY');
                            handleSubmit();
                          }}
                          type="button"
                        >
                          {t('Confirm delivery')}
                        </GhostButton>
                        {deliveryQuantityApprovalRequired &&
                          Confirm(
                            t('Delivery Quantity'),
                            t(
                              'Delivery quantity is greater than was responded. Are you sure, you want to confirm?'
                            ),
                            async () => {
                              setDeliveryQuantityApprovalRequired(false);
                              onDeliverySubmit(values.delivery, {
                                setSubmitting
                              });
                            },
                            () => {
                              setDeliveryQuantityApprovalRequired(false);
                              setSubmitting(false);
                            }
                          )}
                        {values.delivery.canRollBack && (
                          <GhostButton
                            disabled={isSubmitting || disabled}
                            onClick={async () => {
                              Confirm(
                                t('Rollback'),
                                t(
                                  'Are you sure you want to change service request status back to "Confirmed"?'
                                ),
                                async () => {
                                  await onDeliveryRollback();
                                }
                              );
                            }}
                            type="button"
                          >
                            {t('Rollback')}
                          </GhostButton>
                        )}
                      </td>
                    </tr>
                  )}
                  {assignmentDelivery && (
                    <tr>
                      <td>
                        <Quantity amount={assignmentDelivery.quantity} />
                      </td>
                      <td>
                        <span>{exerciseCurrency}</span>
                      </td>
                      <td>
                        <Money amount={assignmentDelivery.unit} />
                      </td>
                      <td>
                        <Money amount={assignmentDelivery.total} />
                      </td>
                      <td>
                        <span>{assignmentDelivery.percentage}%</span>
                      </td>
                      <td>
                        <Money amount={assignmentDelivery.grossAmount} />
                      </td>
                      <td className="comment">{assignmentDelivery.comment}</td>
                      <td className="small">
                        <div
                          onClick={onDeliveryFileDialogOpen}
                          className="response-attachment-link"
                        >
                          <Icon
                            icon="attachment"
                            className={cx('files-icon', {
                              'no-files': !assignmentDelivery.attachments.length
                            })}
                          />
                          {assignmentDelivery.attachments.length
                            ? `(${assignmentDelivery.attachments.length})`
                            : ''}
                        </div>
                      </td>
                      <td></td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
              {values.sharedCost && values.sharedCost.isSharedCost && disabled && (
                <span className="shared-cost-message">
                  <span className="icon">!</span>
                  <span className="cost-message">
                    {t('This service is grouped as shared cost')}
                  </span>
                </span>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

AssignmentResponse.propTypes = {
  delivery: PropTypes.object,
  assignmentDelivery: PropTypes.object,
  assignmentResponse: PropTypes.object,
  t: PropTypes.func,
  onSubmit: PropTypes.func,
  onDeliverySubmit: PropTypes.func,
  vatOptions: PropTypes.array,
  getSelectedVat: PropTypes.func,
  disabled: PropTypes.bool,
  isFileDialogOpen: PropTypes.bool,
  readOnly: PropTypes.bool,
  onFileDialogClose: PropTypes.func,
  onFileSelect: PropTypes.func,
  isDeliveryFileDialogOpen: PropTypes.bool,
  onDeliveryFileDialogOpen: PropTypes.func,
  onDeliveryFileDialogClose: PropTypes.func,
  onSharedCostChange: PropTypes.func,
  onEarlyConfirmationRequiredChange: PropTypes.func,
  onDeliveryFinalizedChange: PropTypes.func,
  onFileDialogOpen: PropTypes.func,
  onDeliveryRollback: PropTypes.func,
  isEarlyConfirmationVisible: PropTypes.bool
};

export default withRouter(withTranslation()(AssignmentResponse));
